/**
 * Singleton SSE service for AI simulation live updates.
 * Keeps one stream per in-progress simulation so all rows get live updates
 * across component mount/unmount and page reload (sessionStorage restores id).
 */

const STATUS_MAP = {
  0: "Collecting audience data",
  1: "Audience listed",
  2: "Audience selected",
  3: "Persona generated",
  4: "Queries generated",
  5: "Simulation completed",
  6: "Analysis completed",
  9: "Deleted",
  10: "Simulation saved",
  90: "Simulation running",
  [-1]: "Run failed",
};
const getStatusLabel = (code) => STATUS_MAP[code] ?? "Pending";

/** Only these codes keep SSE open and trigger stats refresh; any other status ends the stream. */
export const NON_TERMINAL_STATUSES = [2, 3, 5, 10, 90];

export function isNonTerminalStatus(code) {
  if (code == null || code === "") return false;
  return NON_TERMINAL_STATUSES.includes(Number(code));
}

/** True when SSE should not run (any status outside NON_TERMINAL_STATUSES). */
export function isTerminalStatus(code) {
  return !isNonTerminalStatus(code);
}

let liveStatusBySimulationId = {};
/** Latest numeric status/stage from SSE (mirrors API `status` and progress `stage`). */
let liveStageBySimulationId = {};
let progressBySimulationId = {};
const listeners = new Set();
/** Set of simulation ids we currently have an open stream for. */
const activeStreamIds = new Set();
/** id -> AbortController for each active stream. */
const abortControllers = new Map();
/** Prevents duplicate streams while hydrate + fetch are in flight. */
const connectingStreamIds = new Set();
/** Latest callbacks for a stream. Replaced when another page connects to the same id. */
const streamCallbacks = new Map();

/**
 * Browsers limit parallel connections per host (~6 for HTTP/1.1). Too many SSE
 * streams to the API origin queues XHR/fetch and makes the app feel frozen.
 */
const MAX_PARALLEL_SIMULATION_STREAMS = 4;

const STORAGE_KEY = "mediplusve_active_simulation_id";

function getToken() {
  try {
    const raw = localStorage.getItem("access_token_AS");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function notify() {
  const payload = {
    liveStatusBySimulationId: { ...liveStatusBySimulationId },
    liveStageBySimulationId: { ...liveStageBySimulationId },
    progressBySimulationId: { ...progressBySimulationId },
  };
  listeners.forEach((cb) => {
    try {
      cb(payload);
    } catch (e) {
      console.error("SimulationSSE listener error:", e);
    }
  });
}

function clearForSimulation(sid) {
  const id = String(sid);
  delete liveStatusBySimulationId[id];
  delete liveStageBySimulationId[id];
  delete progressBySimulationId[id];
  try {
    const current = sessionStorage.getItem(STORAGE_KEY);
    if (current === id) sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
  notify();
}

/**
 * Parse one SSE event block (between blank lines). Matches server shape, e.g.:
 *   event: status
 *   data: {"simulationId":"19","status":2}
 * Supports CRLF and multiple data: lines (per spec, joined with \n).
 */
function parseSSEEvent(block) {
  let eventType = "";
  const dataParts = [];
  const lines = block.split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (line.startsWith("event:")) {
      eventType = line
        .replace(/^event:\s*/, "")
        .trim()
        .toLowerCase();
    } else if (line.startsWith("data:")) {
      dataParts.push(line.replace(/^data:\s*/, "").trim());
    } else if (
      !eventType &&
      t &&
      /^(ping|status|connected|error|progress|completed)$/i.test(t)
    ) {
      eventType = t.toLowerCase();
    } else if (
      eventType &&
      dataParts.length === 0 &&
      (t.startsWith("{") || t === "{}")
    ) {
      dataParts.push(t);
    }
  }
  const dataStr = dataParts.join("\n");
  return { eventType, dataStr };
}

function stopStream(streamId, reader, controller) {
  try {
    reader?.cancel?.();
  } catch (_) {}
  try {
    controller?.abort?.();
  } catch (_) {}
}

function callbacksFor(simulationId) {
  return streamCallbacks.get(String(simulationId)) ?? {};
}

async function runStream(simulationId, controller) {
  const streamId = String(simulationId);
  let reader;

  try {
    const token = getToken();
    if (!token) {
      console.warn("SimulationSSE: token missing, stream not started");
      return;
    }

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/simulation/stream/${simulationId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      },
    );

    if (!res.ok) {
      console.error("SimulationSSE HTTP error:", res.status);
      return;
    }
    if (!res.body) {
      console.error("SimulationSSE: response stream not available");
      return;
    }

    reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      let readResult;
      try {
        readResult = await reader.read();
      } catch (readErr) {
        if (readErr.name === "AbortError") return;
        console.error("SimulationSSE read error:", readErr);
        return;
      }

      const { value, done } = readResult;
      if (done) break;

      try {
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop();

        for (const block of blocks) {
          const trimmed = block.trim();
          if (!trimmed) continue;
          const { eventType, dataStr } = parseSSEEvent(trimmed);
          if (eventType === "ping") continue;
          /* connected: handshake only; do not overwrite status/progress if server repeats it */
          if (eventType === "connected") continue;
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            const sid =
              data.simulationId != null ? String(data.simulationId) : streamId;
            if (eventType === "completed") {
              const id = sid || streamId;
              const stageNum =
                data.stage != null && Number.isFinite(Number(data.stage))
                  ? Number(data.stage)
                  : 6;
              const label =
                (typeof data.message === "string" && data.message.trim()) ||
                getStatusLabel(stageNum);
              liveStatusBySimulationId = {
                ...liveStatusBySimulationId,
                [id]: label,
              };
              liveStageBySimulationId = {
                ...liveStageBySimulationId,
                [id]: stageNum,
              };
              const doneCallbacks = callbacksFor(id);
              if (typeof doneCallbacks.onStatsRequest === "function") doneCallbacks.onStatsRequest();
              if (typeof doneCallbacks.onTerminal === "function") doneCallbacks.onTerminal(id);
              const { [id]: _p, ...restProg } = progressBySimulationId;
              progressBySimulationId = restProg;
              try {
                const current = sessionStorage.getItem(STORAGE_KEY);
                if (current === id) sessionStorage.removeItem(STORAGE_KEY);
              } catch (_) {}
              notify();
              if (id === streamId) {
                stopStream(streamId, reader, controller);
                return;
              }
            } else if (eventType === "status" && sid && data.status != null) {
              const n = Number(data.status);
              const statusLabel =
                typeof data.message === "string" && data.message.trim()
                  ? data.message.trim()
                  : getStatusLabel(data.status);
              liveStatusBySimulationId = {
                ...liveStatusBySimulationId,
                [sid]: statusLabel,
              };
              liveStageBySimulationId = {
                ...liveStageBySimulationId,
                [sid]: n,
              };
              if (isNonTerminalStatus(n)) {
                const progressCallbacks = callbacksFor(sid);
                if (typeof progressCallbacks.onStatsRequest === "function") progressCallbacks.onStatsRequest();
                notify();
              } else {
                const terminalCallbacks = callbacksFor(sid);
                if (typeof terminalCallbacks.onTerminal === "function") terminalCallbacks.onTerminal(sid);
                const { [sid]: _p, ...restProg } = progressBySimulationId;
                progressBySimulationId = restProg;
                try {
                  const current = sessionStorage.getItem(STORAGE_KEY);
                  if (current === sid) sessionStorage.removeItem(STORAGE_KEY);
                } catch (_) {}
                notify();
                if (sid === streamId) {
                  stopStream(streamId, reader, controller);
                  return;
                }
              }
            } else if (
              eventType === "progress" &&
              (data.stage != null ||
                data.message != null ||
                data.percentage != null)
            ) {
              const id = sid || streamId;
              if (id) {
                if (data.stage != null && !isNonTerminalStatus(data.stage)) {
                  const st = Number(data.stage);
                  const statusLabel =
                    typeof data.message === "string" && data.message.trim()
                      ? data.message.trim()
                      : getStatusLabel(data.stage);
                  liveStatusBySimulationId = {
                    ...liveStatusBySimulationId,
                    [id]: statusLabel,
                  };
                  liveStageBySimulationId = {
                    ...liveStageBySimulationId,
                    [id]: st,
                  };
                  const stageCallbacks = callbacksFor(id);
                  if (typeof stageCallbacks.onTerminal === "function") stageCallbacks.onTerminal(id);
                  const { [id]: _p, ...rest } = progressBySimulationId;
                  progressBySimulationId = rest;
                  try {
                    const current = sessionStorage.getItem(STORAGE_KEY);
                    if (current === id) sessionStorage.removeItem(STORAGE_KEY);
                  } catch (_) {}
                  notify();
                  if (id === streamId) {
                    stopStream(streamId, reader, controller);
                    return;
                  }
                } else {
                  const stageNum =
                    data.stage != null && Number.isFinite(Number(data.stage))
                      ? Number(data.stage)
                      : null;
                  const message =
                    (typeof data.message === "string" && data.message.trim()) ||
                    (stageNum != null ? getStatusLabel(stageNum) : null) ||
                    "In progress";
                  const percentage =
                    data.percentage != null ? Number(data.percentage) : null;
                  progressBySimulationId = {
                    ...progressBySimulationId,
                    [id]: { message, percentage },
                  };
                  if (stageNum != null) {
                    liveStatusBySimulationId = {
                      ...liveStatusBySimulationId,
                      [id]: message,
                    };
                    liveStageBySimulationId = {
                      ...liveStageBySimulationId,
                      [id]: stageNum,
                    };
                  }
                  notify();
                }
              }
            }
          } catch (_) {
            if (dataStr !== "{}")
              console.error("SimulationSSE JSON parse error:", dataStr);
          }
        }
      } catch (chunkErr) {
        console.error("SimulationSSE chunk error:", chunkErr);
        buffer = "";
      }
    }
  } catch (err) {
    if (err.name !== "AbortError")
      console.error("SimulationSSE fatal error:", err);
  } finally {
    activeStreamIds.delete(streamId);
    abortControllers.delete(streamId);
  }
}

/**
 * Abort SSE for simulations not in `desiredIds` (e.g. left the current page).
 * Frees connection slots so other API calls are not queued behind idle streams.
 */
/** Stop one simulation stream and drop its callbacks. */
export function disconnect(simulationId) {
  const id = simulationId == null ? null : String(simulationId);
  if (!id) return;
  streamCallbacks.delete(id);
  connectingStreamIds.delete(id);
  activeStreamIds.delete(id);
  const controller = abortControllers.get(id);
  abortControllers.delete(id);
  if (controller) controller.abort();
}

export function pruneStreamsNotIn(desiredIds) {
  if (!desiredIds || typeof desiredIds.has !== "function") return;
  const openIds = new Set([...activeStreamIds, ...connectingStreamIds, ...streamCallbacks.keys()]);
  for (const sid of openIds) {
    if (!desiredIds.has(String(sid))) disconnect(sid);
  }
}

/**
 * Connect SSE for the given simulation id. If already connected to this id, does nothing.
 * Multiple simulations can have active streams at the same time; each gets its own connection.
 */
export async function connect(simulationId, options = {}) {
  const id = simulationId == null ? null : String(simulationId);
  if (!id) return;

  streamCallbacks.set(id, {
    onStatsRequest: options.onStatsRequest,
    onTerminal: options.onTerminal,
  });

  if (activeStreamIds.has(id) || connectingStreamIds.has(id)) return;
  if (activeStreamIds.size >= MAX_PARALLEL_SIMULATION_STREAMS) return;

  connectingStreamIds.add(id);
  const { hydrateFromApi = true } = options;

  try {
    if (hydrateFromApi) {
      try {
        const { getAiSimulationDetails } = await import("../../requests");
        const res = await getAiSimulationDetails(id);
        const item = res?.data?.data ?? res?.data;
        if (item && item.status != null) {
          const st = Number(item.status);
          const statusLabel = getStatusLabel(item.status);
          liveStatusBySimulationId = {
            ...liveStatusBySimulationId,
            [id]: statusLabel,
          };
          liveStageBySimulationId = { ...liveStageBySimulationId, [id]: st };
          if (!isNonTerminalStatus(item.status)) {
            const hydrated = callbacksFor(id);
            if (typeof hydrated.onTerminal === "function") hydrated.onTerminal(id);
            clearForSimulation(id);
            streamCallbacks.delete(id);
            return;
          }
          if (!streamCallbacks.has(id)) return;
          notify();
        }
      } catch (e) {
        // ignore; stream will deliver updates
      }
    }

    if (!streamCallbacks.has(id)) return;
    if (activeStreamIds.has(id)) return;
    if (activeStreamIds.size >= MAX_PARALLEL_SIMULATION_STREAMS) return;

    const controller = new AbortController();
    abortControllers.set(id, controller);
    activeStreamIds.add(id);

    runStream(id, controller).catch(() => {});
  } finally {
    connectingStreamIds.delete(id);
  }
}

/**
 * Subscribe to live state updates. Callback receives { liveStatusBySimulationId, progressBySimulationId }.
 * Returns unsubscribe function.
 */
export function subscribe(callback) {
  listeners.add(callback);
  callback({
    liveStatusBySimulationId: { ...liveStatusBySimulationId },
    liveStageBySimulationId: { ...liveStageBySimulationId },
    progressBySimulationId: { ...progressBySimulationId },
  });
  return () => listeners.delete(callback);
}

/**
 * Current state (for initial render / reload).
 */
export function getState() {
  return {
    liveStatusBySimulationId: { ...liveStatusBySimulationId },
    liveStageBySimulationId: { ...liveStageBySimulationId },
    progressBySimulationId: { ...progressBySimulationId },
  };
}

/**
 * Active simulation id from sessionStorage (survives reload).
 */
export function getActiveSimulationId() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? String(saved).trim() || null : null;
  } catch {
    return null;
  }
}

export function setActiveSimulationId(id) {
  try {
    if (id == null || id === "") sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(id));
  } catch (_) {}
}
